import React, { useEffect, useRef, useState } from "react";
import { MAX_NOTE_LENGTH, updateNote } from "../../utils/myStudy";
import { openAccount, useAccount } from "../../firebase/account";

/*
 * The note on a saved item, shared by the My Study page and the note panel
 * beside each Save button, so a note written on the page being studied is the
 * same note My Study shows.
 *
 * Keeps its own copy of the text while the reader types and writes it back
 * shortly after they stop, and again on blur and on close. Writing on every
 * keystroke would re-render every save button and saved card through the
 * change event for each letter.
 */
export default function StudyNoteField({
  itemKey,
  note,
  label,
  autoFocus = true,
}) {
  const [value, setValue] = useState(note || "");
  const [status, setStatus] = useState("");
  const timerRef = useRef(null);
  const lastSavedRef = useRef(note || "");
  const valueRef = useRef(value);

  valueRef.current = value;

  /* Writing notes needs an account, the same as saving. A guest can still
     have items saved in this browser from before accounts; they see their
     note but sign in to change it, and it moves into the account when they do. */
  const account = useAccount();
  const guest = account.status === "signedOut" || account.status === "loading";

  /* Flushes on unmount, so closing the panel mid-sentence keeps the sentence. */
  useEffect(
    () => () => {
      window.clearTimeout(timerRef.current);
      if (valueRef.current !== lastSavedRef.current) {
        updateNote(itemKey, valueRef.current);
      }
    },
    [itemKey]
  );

  function commit(text) {
    window.clearTimeout(timerRef.current);
    if (text === lastSavedRef.current) return;

    updateNote(itemKey, text);
    lastSavedRef.current = text;
    setStatus("Saved");
  }

  if (guest) {
    return (
      <div className="ctb-studyNote ctb-studyNote--guest">
        {note ? <p className="ctb-studyNote-readOnly">{note}</p> : null}
        <button
          type="button"
          className="ctb-studyNote-signIn"
          onClick={() =>
            openAccount({
              mode: "signIn",
              reason:
                "Sign in to write notes. Your notes are kept in your account, on any device you sign in on.",
            })
          }
        >
          Sign in to {note ? "edit this note" : "add a note"}
        </button>
      </div>
    );
  }

  return (
    <div className="ctb-studyNote">
      <textarea
        value={value}
        maxLength={MAX_NOTE_LENGTH}
        rows={3}
        autoFocus={autoFocus}
        placeholder="Add a note…"
        aria-label={`Note for ${label}`}
        onChange={(event) => {
          const text = event.target.value;
          setValue(text);
          setStatus("");
          window.clearTimeout(timerRef.current);
          timerRef.current = window.setTimeout(() => commit(text), 700);
        }}
        onBlur={() => commit(value)}
      />
      <span className="ctb-studyNote-status" role="status" aria-live="polite">
        {status}
      </span>
    </div>
  );
}
